/**
 * Centralized domain mappings.
 *
 * Single source of truth for sentiment, gender, SAC type,
 * and conversation field mappings used across multiple features.
 */

// ── Domain types ──────────────────────────────────────────────────────────────

export type Sentiment = 'Positivo' | 'Negativo' | 'Neutro';
export type Gender = 'man' | 'woman' | 'company';
export type NormalizedSentiment = 'positive' | 'negative' | 'neutral';

// ── Polarity (API numeric string) ↔ Normalized sentiment ─────────────────────

export const POLARITY_TO_NORMALIZED_SENTIMENT: Record<string, 'positive' | 'negative' | 'neutral'> = {
  '1': 'positive',
  '2': 'negative',
  '3': 'neutral',
};

export const NORMALIZED_SENTIMENT_TO_POLARITY: Record<string, string> = {
  positive: '1',
  negative: '2',
  neutral: '3',
};

// ── Polarity (API numeric string) → Portuguese Sentiment label ────────────────

export const SENTIMENT_FROM_POLARITY: Record<string, Sentiment> = {
  '1': 'Positivo',
  '2': 'Negativo',
  '3': 'Neutro',
};

// ── Portuguese sentiment label ↔ Normalized sentiment ────────────────────────

export const SENTIMENT_LABEL_TO_NORMALIZED: Record<string, 'positive' | 'negative' | 'neutral'> = {
  Positivo: 'positive',
  Negativo: 'negative',
  Neutro: 'neutral',
};

export const NORMALIZED_SENTIMENT_TO_LABEL: Record<string, Sentiment> = {
  positive: 'Positivo',
  negative: 'Negativo',
  neutral: 'Neutro',
};

// ── Sentiment display ─────────────────────────────────────────────────────────

export const SENTIMENT_COLORS: Record<Sentiment, string> = {
  Negativo: '#a80b0b',
  Neutro: 'var(--color-content-default, rgba(40, 40, 40, 1))',
  Positivo: '#007a42',
};

export const SENTIMENT_ICONS: Record<Sentiment, string> = {
  Negativo: 'emoji-negative',
  Neutro: 'emoji-neutral',
  Positivo: 'emoji',
};

export const SENTIMENT_OPTIONS: Sentiment[] = ['Negativo', 'Neutro', 'Positivo'];

// ── Gender label (API) → type ─────────────────────────────────────────────────

export const GENDER_LABEL_TO_TYPE: Record<string, Gender> = {
  Homem: 'man',
  Mulher: 'woman',
  Marca: 'company',
  'Organização': 'company',
  '1': 'man',
  '2': 'woman',
  '3': 'company',
};

// ── Gender display ────────────────────────────────────────────────────────────

export const GENDER_OPTIONS: Gender[] = ['man', 'woman', 'company'];

export const GENDER_ICONS: Record<Gender, string> = {
  man: 'user-default',
  woman: 'user-woman',
  company: 'company',
};

// ── Gender number (API genders array) → i18n key ──────────────────────────────

export const GENDER_NUMBER_TO_I18N_KEY: Record<number, string> = {
  1: 'globalFilter.genderMan',
  2: 'globalFilter.genderWom',
  3: 'globalFilter.genderOrg',
};

// ── SAC type → BDS icon name ──────────────────────────────────────────────────

export const SAC_TYPE_TO_ICON: Record<string, string> = {
  mention: 'mail',
  inbox: 'send',
  postcomment: 'builder-text-message',
  mentioncomment: 'builder-text-message',
  externalcomment: 'builder-text-message',
  reviewcomment: 'builder-text-message',
  visitorcomment: 'builder-text-message',
};

// ── SAC type → i18n tooltip key ───────────────────────────────────────────────

export const SAC_TYPE_TO_TOOLTIP_KEY: Record<string, string> = {
  mention: 'cardPosts.sacTooltipMention',
  inbox: 'cardPosts.sacTooltipInbox',
  postcomment: 'cardPosts.sacTooltipComment',
};

// ── Conversation criticality API value → UI key ───────────────────────────────

export const CONVERSATION_CRITICALITY_VALUE_TO_KEY: Record<string, string> = {
  '0': 'criticality-none',
  '1': 'criticality-low',
  '2': 'criticality-medium',
  '3': 'criticality-high',
};

// ── Conversation status API value → UI key ────────────────────────────────────

export const CONVERSATION_STATUS_VALUE_TO_KEY: Record<string, string> = {
  Ignorado: 'status-ignored',
  Pendente: 'status-pending',
  Aberto: 'status-open',
  'Em espera': 'status-waiting',
  Respondido: 'status-replied',
  Fechado: 'status-closed',
};

// ── NormalizedSentiment icons ─────────────────────────────────────────────────

export const NORMALIZED_SENTIMENT_ICONS: Record<NormalizedSentiment, string> = {
  positive: 'emoji',
  negative: 'emoji-negative',
  neutral: 'emoji-neutral',
};

// ── Post card actions ─────────────────────────────────────────────────────────

export type PostCardActionType = 'copy-id' | 'copy-link' | 'export';

export const POST_CARD_ACTION_ICONS: Record<PostCardActionType, string> = {
  'copy-id': 'copy',
  'copy-link': 'link',
  'export': 'download',
};

